import { useApiQuery } from "@/api/hooks";
import { getAncestors } from "@/entities/FileService/File.queries";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { selectedPeriodAtom } from "./metrics.state";
import { useRouter } from "@/utils/router";
import clsx from "clsx";
import { useSetAtom } from "jotai";
import { ConnectorType } from "@/entities/DataAssociation/File.model";
import { ConnectorIcon } from "@/features/Labeling/components/ConnectorIcon";
import { DocumentTextIcon } from "@heroicons/react/24/outline";

export function AncestorTree({ fileId }: { fileId: string }) {
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);
    const setSelectedPeriod = useSetAtom(selectedPeriodAtom);
    const router = useRouter();
    const { data: tree, isLoading } = useApiQuery(getAncestors, {
        params: { fileId },
    });

    return (
        <div
            className={clsx("mt-4 w-full", "flex flex-col", "gap-2 break-all")}
        >
            <p className="heading-m mb-1">Ancestor files</p>

            {!tree && isLoading && (
                <>
                    {[...Array(3)].map((_, i) => (
                        <div
                            key={i}
                            className={clsx(
                                "bg-selected h-20 w-full animate-pulse self-center rounded",
                            )}
                        />
                    ))}
                </>
            )}

            {tree && (
                <div>
                    <div className={clsx("p-2 pb-0", "flex flex-col", "gap-2")}>
                        <div className="flex items-center justify-between gap-2">
                            {tree.root.sourceConnectorType ? (
                                <ConnectorIcon
                                    connectorType={
                                        tree.root
                                            .sourceConnectorType as ConnectorType
                                    }
                                    condensed={false}
                                />
                            ) : (
                                <div className="flex items-center gap-1">
                                    <DocumentTextIcon
                                        height={20}
                                        width={18}
                                        className="fill-[#55C289] stroke-slate-800"
                                    />
                                    <p className="body-s capitalize">
                                        File upload
                                    </p>
                                </div>
                            )}
                        </div>
                        <p className="body-s break-all pr-1 ">
                            {tree.root.name}
                        </p>
                    </div>
                    <ul className=" p-2 pl-4">
                        {tree.children.map((child) => (
                            <div key={child.id}>
                                <div
                                    className={clsx(
                                        "mb-2 mt-3",
                                        "flex flex-col",
                                        "gap-2",
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className={clsx(
                                                "ml-1 bg-slate-600",
                                                "size-3 shrink-0 rounded-full",
                                            )}
                                        />
                                        <p className="body-micro">
                                            {child.name.startsWith("cl-")
                                                ? "Cleaned"
                                                : "Labeled"}
                                        </p>
                                    </div>

                                    <li
                                        className={clsx(
                                            "ml-2 p-2 px-4",
                                            "body-s cursor-pointer rounded-lg",
                                            "hover:bg-hover",
                                        )}
                                        onClick={() => {
                                            setSelectedPeriod(null);
                                            setRightDrawerOpen(false);
                                            setTimeout(() => {
                                                router.navigate(
                                                    `/beta/companies/${router.params.companyId}/labeling/files/${child.id}`,
                                                );
                                            }, 0);
                                        }}
                                    >
                                        {child.name}
                                    </li>
                                </div>
                            </div>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
